import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getMessageById, updateMessage } from "../services/messageService";
import { MessageDetailResponse, MessageUpdateInputForm, MessageUpdateRequest, ScheduleCycleDetailResponse, ScheduleUpdateInputForm } from "../types/MessageTypes";
import "../styles/form.css";
import "../styles/update.css";
import { FamilyResponse } from "../types/FamilyTypes";
import { getFamilies } from "../services/familyService";

const MessageDetail = () => {
  const { id } = useParams<{ id: string }>();

  const [message, setMessage] = useState<MessageDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [scheduledPlansToDelete, setScheduledPlansToDelete] = useState<number[]>([]);
  const [families, setFamilies] = useState<FamilyResponse[]>([]);

  const [currentMessage, setCurrentMessage] = useState<MessageUpdateInputForm>({
    id: 0,
    sentBody: "",
    familyId: 0,
    schedules: []
  });

  const navigate = useNavigate();

      
  function getNumDays(startDate: string, endDate: string) {
    const scheduledStartTime = new Date(startDate);
    const scheduledEndTime = new Date(endDate);

    const diffInMs = scheduledEndTime.getTime() - scheduledStartTime.getTime();

    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    return Math.floor(diffInDays);
  }

  const convertUtcToLocal = (utcDateString: string) => {
    const date = new Date(utcDateString + "Z"); // Ensure UTC
    if (isNaN(date.getTime())) return "";
  
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
  
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {

    const fetchMessage = async () => {
      try {
        const res = await getMessageById(id!);
        if (res === null)
        {
          throw ": Message does not exist";
        }
        // Apply conversion to each plan inside the message
  
        setCurrentMessage({
          id: res.id,
          sentBody: res.sentBody,
          familyId: res.familyId,
          schedules: res.scheduledPlans.map((schedule) => ({
            id: schedule.id,
            isRecurred: schedule.isRecurred,
            scheduledStartTime: convertUtcToLocal(schedule.scheduledStartTime),
            numDays: getNumDays(schedule.scheduledStartTime, schedule.scheduledEndTime)
          }))
        });
      } catch (error) {
        console.error("Failed to load message", error);
      } finally {
        setLoading(false);
      }
    };
  
    async function fetchFamily() {
      const data = await getFamilies();
      setFamilies(data);
    }
    
    fetchFamily();
    fetchMessage();
  }, [id]);

  const calculateEndDate = (date: Date, numDays: number) => {
    const endDate = new Date(date);
    endDate.setDate(endDate.getDate() + Number(numDays));
    return endDate;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    if (!currentMessage) return;
    setCurrentMessage({ ...currentMessage, [e.target.name]: e.target.value });
  };

  const handlePlanChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!currentMessage) return;
    const updated = [...currentMessage.schedules];
    updated[index] = {
      ...updated[index],
      [e.target.name]: e.target.type === "checkbox" ? (e.target as HTMLInputElement).checked : e.target.value
    };
    setCurrentMessage({ ...currentMessage, schedules: updated });
  };

  const handleDeletePlan = (planId: number) => {
    if (!currentMessage) return;
    setScheduledPlansToDelete([...scheduledPlansToDelete, planId]);
    setCurrentMessage({
      ...currentMessage,
      schedules: currentMessage.schedules.filter((plan) => plan.id !== planId),
    });
  };

  const handleAddNewPlan = () => {
    if (!currentMessage) return;
    const tempId = -Math.floor(Math.random() * 1000);
    const newPlan: ScheduleUpdateInputForm = {
      id: tempId,
      isRecurred: false,
      scheduledStartTime: "",
      numDays: 0
    };
    setCurrentMessage({ ...currentMessage, schedules: [...currentMessage.schedules, newPlan] });
  };

  const handleSubmit = async () => {
    try {
        const payload: MessageUpdateRequest = {
              id: currentMessage.id,
              sender: "Sage Pharmacy",
              sentBody: currentMessage.sentBody,
              familyId: Number(currentMessage.familyId),
              deletePlanIds: scheduledPlansToDelete,
              scheduledPlans: currentMessage.schedules.map((schedule) => ({
                id: Number(schedule.id),
                isRecurred: schedule.isRecurred,
                scheduledStartTime: new Date(schedule.scheduledStartTime).toISOString(),
                scheduledEndTime: calculateEndDate(new Date(schedule.scheduledStartTime), schedule.numDays).toISOString(),
                scheduleCycle: 1,
                frequency: "Daily"
              }))
        };
      
        const response = await updateMessage(payload);
        if (response === null)
        {
          alert("Failed to update message.");
          return;
        }
        alert("Message updated successfully");
        navigate("/");
    } catch (error) {
        console.error("Update failed", error);
        alert("Failed to update message");
    }
  };

  if (loading) return <p>Loading...</p>;
  if (!currentMessage) return <p>Message not found.</p>;


  return (
    <div className="form-container update-form-container">
        <h2 className="text-xl font-bold mb-4">Edit Message</h2>
        <div className="update-message-form">
            <div className="input-label-group">
                <label>Message Body: </label>
                <textarea name="sentBody" value={currentMessage.sentBody} rows={5} onChange={handleChange} placeholder="Body" className="border p-2 w-full mb-2" />
            </div>

            <div className="input-label-group">
                <label>Family Group: </label>
                <select name="familyId" value={currentMessage.familyId} onChange={handleChange} required>
                    <option value={0}>Please select family</option>
                    {families && families.map((family) => (
                        <option key={family.id} value={family.id}>
                            {family.familyName}
                        </option>
                    ))}
                </select>   
            </div>
        </div>

        <div className="scheduledPlan-header">
            <h3 className="font-semibold">Scheduled Plans</h3>
            <button onClick={handleAddNewPlan} className="bg-green-500 text-white px-3 py-1 rounded mt-2">+ Add Another Schedule</button>
        </div>

        {currentMessage.schedules.map((schedule, index) => (
            <div key={index} className="border p-2 mb-2 update-schedule">
                {/* <label>
                  <input
                      type="checkbox"
                      name="isRecurred"
                      checked={schedule.isRecurred}
                      onChange={(e) => handlePlanChange(index, e)}
                  />
                  Recurred
                </label> */}
                <div className="input-row schedule-input-row">
                  <p>Send a message daily at </p>
                  <input
                    type="datetime-local"
                    name="scheduledStartTime"
                    value={schedule.scheduledStartTime}
                    onChange={(e) => handlePlanChange(index, e)}
                    required
                  />
                  <p> for </p>
                  <input
                    type="number"
                    name="numDays"
                    placeholder="Number of days"
                    value={schedule.numDays}
                    onChange={(e) => handlePlanChange(index, e)}
                    required
                  />
                  <p> days</p>
              </div>
              <button onClick={() => handleDeletePlan(schedule.id)} className="text-red-500 text-sm delete-button">Delete</button>
            </div>
        ))}

        <button onClick={handleSubmit} className="bg-blue-500 text-white px-5 py-2 rounded mt-4 block">Save Changes</button>
    </div>
  );
};

export default MessageDetail;
